import {
  V2ListPromptOverridesForAccountApiArg,
  useV2ListPromptOverridesForAccountQuery,
} from "@generated";
import type { PromptOverride } from "../types";

export interface HookArgs {
  page?: V2ListPromptOverridesForAccountApiArg["page"];
}

const useListPromptOverrides = ({ page }: HookArgs) => {
  const { data, error, isLoading, refetch } =
    useV2ListPromptOverridesForAccountQuery({
      page,
      include: ["open-router-key"],
    });

  let promptOverrides: PromptOverride[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      const included = (data as any)?.included || [];

      promptOverrides = data.data.map((item) => {
        if (!item.id) {
          throw new Error("Missing prompt override id");
        }

        const attrs = item.attributes as Record<string, any> | undefined;
        const rels = item.relationships as Record<string, any> | undefined;
        const openRouterKeyId = rels?.["open-router-key"]?.data?.id;

        let openRouterKeyName = "";
        if (openRouterKeyId) {
          const keyResource = included.find(
            (inc: any) =>
              inc.type === "open-router-keys" &&
              String(inc.id) === String(openRouterKeyId)
          );
          openRouterKeyName = keyResource?.attributes?.name || "";
        }

        return {
          id: String(item.id),
          promptType: attrs?.["prompt-type"] || "",
          content: attrs?.["content"] || "",
          model: Array.isArray(attrs?.["model"])
            ? (attrs as Record<string, any>)["model"]
            : [],
          openRouterKeyName,
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    promptOverrides,
    error: error || customError,
    isLoading,
    pageMeta,
    refetch,
  };
};

export default useListPromptOverrides;
